import React, { useEffect, useState } from 'react';
import { toast } from 'react-toastify';
import { showUserErrorToast } from '../../utils/userToast';
import {
  fetchPreferredBilling,
  getPreferredBillingErrorMessage,
  PreferredBillingMethod,
  updatePreferredBillingMethod,
} from '../../services/preferredBillingService';
import { useLiveDataRefresh } from '../../hooks/useLiveDataRefresh';
import './PreferredBillingToggle.css';

type Props = {
  userId: string | null | undefined;
  className?: string;
  /** When true (free / never card-paid), credits stay locked even before the API responds. */
  requireCardFirst?: boolean;
  onChange?: (
    method: PreferredBillingMethod,
    meta?: { creditsAllowed: boolean }
  ) => void;
};

const DEFAULT_CARD_FIRST_NOTE =
  'Your first subscription must be paid by card. After that, you can use available wallet credits for renewals and upgrades.';

const PreferredBillingToggle: React.FC<Props> = ({
  userId,
  className,
  requireCardFirst = false,
  onChange,
}) => {
  const [method, setMethod] = useState<PreferredBillingMethod>('card');
  const [creditsAllowed, setCreditsAllowed] = useState(!requireCardFirst);
  const [blockedMessage, setBlockedMessage] = useState<string | null>(
    requireCardFirst ? DEFAULT_CARD_FIRST_NOTE : null
  );
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  const loadPreferredBilling = React.useCallback(
    async (options?: { silent?: boolean }) => {
      if (!userId) {
        setLoading(false);
        return;
      }
      if (!options?.silent) {
        setLoading(true);
      }
      try {
        const state = await fetchPreferredBilling(userId);
        const allowed = requireCardFirst ? false : state.creditsAllowed;
        setCreditsAllowed(allowed);
        setBlockedMessage(
          allowed ? null : state.creditsBlockedMessage || DEFAULT_CARD_FIRST_NOTE
        );
        const nextMethod = allowed ? state.preferredBillingMethod : 'card';
        setMethod(nextMethod);
        onChange?.(nextMethod, { creditsAllowed: allowed });
      } catch (error) {
        if (!options?.silent) {
          showUserErrorToast(
            getPreferredBillingErrorMessage(error, 'Could not load default payment method.')
          );
        }
      } finally {
        if (!options?.silent) {
          setLoading(false);
        }
      }
    },
    // Intentionally omit onChange — parent often passes an inline handler.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [userId, requireCardFirst]
  );

  useEffect(() => {
    loadPreferredBilling();
  }, [loadPreferredBilling]);

  useLiveDataRefresh(() => loadPreferredBilling({ silent: true }), { enabled: Boolean(userId) });

  const selectMethod = async (next: PreferredBillingMethod) => {
    if (!userId || next === method || saving) return;
    if (next === 'credits' && !creditsAllowed) {
      toast.info(blockedMessage || DEFAULT_CARD_FIRST_NOTE);
      return;
    }
    const previous = method;
    setMethod(next);
    setSaving(true);
    try {
      const result = await updatePreferredBillingMethod(userId, next);
      setMethod(result.preferredBillingMethod);
      onChange?.(result.preferredBillingMethod, { creditsAllowed });
      toast.success(
        result.message ||
          (result.preferredBillingMethod === 'credits'
            ? 'Renewals and upgrades will use your available wallet balance (card will not be auto-charged).'
            : 'Renewals will charge your card.')
      );
    } catch (error) {
      setMethod(previous);
      showUserErrorToast(
        getPreferredBillingErrorMessage(error, 'Could not update default payment method.')
      );
    } finally {
      setSaving(false);
    }
  };

  if (!userId) return null;

  return (
    <section className={`preferred-billing ${className || ''}`.trim()}>
      <div className="preferred-billing__copy">
        <h3>Default payment method</h3>
        <p>
          Choose how Songplace renews and upgrades your subscription. Credits use only your{' '}
          <strong>available</strong> wallet balance (not eligible or pending earnings) and do not
          charge your card when the balance covers the amount.
        </p>
        {!creditsAllowed ? (
          <p className="preferred-billing__note" role="note">
            {blockedMessage || DEFAULT_CARD_FIRST_NOTE}
          </p>
        ) : null}
      </div>
      <div className="preferred-billing__toggle" role="group" aria-label="Default payment method">
        <button
          type="button"
          className={`preferred-billing__option${method === 'credits' ? ' is-active' : ''}`}
          disabled={loading || saving || !creditsAllowed}
          title={!creditsAllowed ? blockedMessage || DEFAULT_CARD_FIRST_NOTE : undefined}
          onClick={() => selectMethod('credits')}
        >
          Credits
        </button>
        <button
          type="button"
          className={`preferred-billing__option${method === 'card' ? ' is-active' : ''}`}
          disabled={loading || saving}
          onClick={() => selectMethod('card')}
        >
          Card
        </button>
      </div>
      {loading || saving ? (
        <small className="preferred-billing__status">{loading ? 'Loading…' : 'Saving…'}</small>
      ) : null}
    </section>
  );
};

export default PreferredBillingToggle;
